import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Task as TaskIcon
} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DependencyDialog = ({ 
  open, 
  onClose, 
  currentIssue, 
  allIssues = [],
  onSave 
}) => {
  const [linkType, setLinkType] = useState('is blocked by');
  const [selectedWorkItem, setSelectedWorkItem] = useState('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setLinkType('is blocked by');
      setSelectedWorkItem('');
    }
  }, [open]);

  // Filter out the current issue from available work items
  const availableWorkItems = allIssues.filter(issue => issue.id !== currentIssue?.id);

  const handleLinkTypeChange = (event) => {
    setLinkType(event.target.value);
  };

  const handleWorkItemChange = (event) => {
    setSelectedWorkItem(event.target.value);
  };

  const handleLink = async () => {
    const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    if (selectedWorkItem && linkType) {
      const selectedIssue = availableWorkItems.find(issue => issue.id === selectedWorkItem);
      
      try {
        const response = await fetch(`${API_URL}/api/issues/dependencies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            sourceIssueId: currentIssue.id,
            targetIssueId: selectedWorkItem,
            linkType: linkType
          })
        });
        
        if (response.ok) {
          const dependencyData = {
            sourceIssue: currentIssue,
            targetIssue: selectedIssue,
            linkType: linkType,
            timestamp: new Date().toISOString()
          };
          
          if (onSave) {
            onSave(dependencyData);
          }
        }
      } catch (error) {
        //console.error('Failed to create dependency:', error);
      }
      
      // Reset form and close
      setLinkType('is blocked by');
      setSelectedWorkItem('');
      onClose();
    }
  };

  const handleClose = () => {
    setLinkType('is blocked by');
    setSelectedWorkItem('');
    onClose();
  };

  if (!currentIssue) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Link to related work
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Description */}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Connect work items to indicate the order in which they need to be done.
        </Typography>

        {/* Current Work Item */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            This work item
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 1.5, 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            bgcolor: '#f9f9f9'
          }}>
            <TaskIcon sx={{ color: '#9c27b0', mr: 1, fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {currentIssue.key}
            </Typography>
            <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
              {currentIssue.summary}
            </Typography>
          </Box>
        </Box>

        {/* Link Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Link work item
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {/* Link Type Selector */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel size="small">Select link type</InputLabel>
              <Select
                value={linkType}
                onChange={handleLinkTypeChange}
                label="Select link type"
                size="small"
              >
                <MenuItem value="is blocked by">Is blocked by</MenuItem>
                <MenuItem value="blocks">Blocks</MenuItem>
                <MenuItem value="relates to">Relates to</MenuItem>
                <MenuItem value="duplicates">Duplicates</MenuItem>
                <MenuItem value="is duplicated by">Is duplicated by</MenuItem>
              </Select>
            </FormControl>

            {/* Work Item Selector */}
            <FormControl sx={{ flex: 1 }}>
              <InputLabel size="small">Select a work item</InputLabel>
              <Select
                value={selectedWorkItem}
                onChange={handleWorkItemChange}
                label="Select a work item"
                size="small"
              >
                {availableWorkItems.map((issue) => (
                  <MenuItem key={issue.id} value={issue.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <TaskIcon sx={{ 
                        color: getIssueTypeColor(issue.issueType), 
                        mr: 1, 
                        fontSize: 16 
                      }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        {issue.key}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {issue.summary}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Preview of the relationship */}
        {selectedWorkItem && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Preview
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2">
                <strong>{currentIssue.key}</strong> {linkType} <strong>
                  {availableWorkItems.find(issue => issue.id === selectedWorkItem)?.key}
                </strong>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {availableWorkItems.find(issue => issue.id === selectedWorkItem)?.summary}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          variant="text"
          sx={{ mr: 1 }}
        >
          Close
        </Button>
        <Button
          onClick={handleLink}
          variant="contained"
          disabled={!selectedWorkItem || !linkType}
          sx={{
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function to get issue type color
const getIssueTypeColor = (issueType) => {
  const colors = {
    1: '#ff8a50', // Bug
    2: '#9c27b0', // Epic
    3: '#2196f3', // Story
    4: '#4caf50', // Task
    5: '#ff9800'  // ChangeRequest
  };
  return colors[issueType] || '#4caf50';
};

export default DependencyDialog;